/* global window */

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import '../styles/ProductList.css';
import ProductListItem from './ProductListItem';
import { Link } from 'react-router-dom';
import { array, bool } from 'prop-types';
import AppContext from './context/AppContext';
import { Button } from 'react-bootstrap';
import { MdArrowBackIos, MdArrowForwardIos } from 'react-icons/md';

const ITEMS_PER_VIEW_FOR_MOBILE = 2;
const ITEMS_PER_VIEW_FOR_DESKTOP = 5;
const DEFAULT_ITEM_WIDTH = 250;
const SCROLL_ITEMS_FOR_MOBILE = 2;
const SCROLL_ITEMS_FOR_DESKTOP = 5;
const DEFAULT_X_OFFSET_PERCENT = 2.04;

// Helpers
export const buildProducts = (products) => {
    return products.map((product) => ({
        id: product.id,
        title: product.title,
        vendor: product.vendor,
        imageUrl: product.imageUrl,
        handle: product.handle,
        avgRating: product.avgRating,
        totalReviews: product.totalReviews,
        price: product?.availableInventoryLevels?.at(0)?.inventoryPrice ?? product.price,
        compareAtPrice: product?.availableInventoryLevels?.at(0)?.inventoryCompareAtPrice ?? product.compareAtPrice,
        availableForSale: !!product?.availableInventoryLevels?.at(0)?.availableForSale,
        availableQuantity: product?.availableInventoryLevels?.at(0)?.availableQuantity ?? 0,
        bypassNegativeQuantity: product?.availableInventoryLevels?.at(0)?.bypassNegativeQuantity ?? false,
        inventoryLevelId: product?.availableInventoryLevels?.at(0)?.id ?? null,
    }));
};

export default function ProductList({ isMobile, products, featured, minimized, alignLeft, historyMode, addToOrder, orderId }) {
    const { appContext } = useContext(AppContext);
    const appConfig = appContext.config.themeConfig;
    const productListRef = useRef(null);
    const [selected, setSelected] = useState(0);
    const [isSwitching, setIsSwitching] = useState(false);
    const [startX, setStartX] = useState(0);
    const [offsetXPercent, setOffsetXPercent] = useState(0);
    const [productCount, setProductCount] = useState(0);

    useEffect(() => {
        if (products.length > 0) {
            let baseCount = products.length;
            if (featured === true) {
                baseCount += 1;
            }
            setProductCount(baseCount);
        }
    }, [products]);

    // Pre-calculate the number of items per view and the width adjustment
    const ITEMS_PER_VIEW = isMobile ? ITEMS_PER_VIEW_FOR_MOBILE : ITEMS_PER_VIEW_FOR_DESKTOP;
    const SCROLL_ITEMS = isMobile ? SCROLL_ITEMS_FOR_MOBILE : SCROLL_ITEMS_FOR_DESKTOP;
    const USE_SLIDER = (isMobile === true || historyMode === true) && productCount > ITEMS_PER_VIEW;
    const ITEM_WIDTH = DEFAULT_ITEM_WIDTH;
    const CONTAINER_WIDTH = ITEM_WIDTH * (featured === true ? (productCount + 1) : productCount);

    /**
     * Extract product data needed for the ProductLineItem, including the
     * inventory level that has an available quantity that has not already
     * been added to the cart.
     */
    const productData = useMemo(() => buildProducts(products), [products]);

    const prev = useCallback(() => {
        let prevIndex;
        if ((selected - SCROLL_ITEMS) >= 0) {
            prevIndex = selected - SCROLL_ITEMS;
        } else if (selected === 0) {
            prevIndex = productCount - 1;
        } else {
            prevIndex = 0;
        }
        setSelected(prevIndex);
    }, [selected, productCount, SCROLL_ITEMS]);

    const next = useCallback(() => {
        let nextIndex;
        if ((selected + SCROLL_ITEMS) < productCount) {
            nextIndex = selected + SCROLL_ITEMS;
        } else if (selected === productCount - 1) {
            nextIndex = 0;
        } else {
            nextIndex = productCount - 1;
        }
        setSelected(nextIndex);
    }, [selected, productCount, SCROLL_ITEMS]);


    useEffect(() => {
        /* Manual switch */
        setOffsetXPercent(0);
        const handleTouchMove = (e) => {
            if (USE_SLIDER === true && isSwitching === true) {
                const newX = e.touches[0].clientX;
                const productListWidth = Math.floor(productListRef.current.offsetWidth / (featured === true ? (productCount + 1) : productCount));
                // Check difference
                const diffX = (newX - startX);
                const newOffsetXPercent = Math.floor(diffX / productListWidth * 100);
                const SWITCH_DIFF_PERCENT = 15;
                if (newOffsetXPercent > SWITCH_DIFF_PERCENT) {
                    prev();
                    setIsSwitching(false);
                } else if (newOffsetXPercent <= -SWITCH_DIFF_PERCENT) {
                    next();
                    setIsSwitching(false);
                } else {
                    setOffsetXPercent(newOffsetXPercent * DEFAULT_X_OFFSET_PERCENT);
                }
            }
        };
        window.addEventListener('touchmove', handleTouchMove);
        return () => {
            window.removeEventListener('touchmove', handleTouchMove);
        };
    }, [selected, isSwitching, products, USE_SLIDER]);
    const handleTouchStart = (e) => {
        e.preventDefault();
        if (!USE_SLIDER) {
            return;
        }
        setIsSwitching(true);
        setOffsetXPercent(0);
        setStartX(e.touches[0].clientX);
    };
    const handleTouchEnd = (_e) => {
        if (!USE_SLIDER) {
            return;
        }
        setIsSwitching(false);
    };
    return (<section
        className={'product-list--container d-flex'}
        style={{
            ...USE_SLIDER && ({
                overflowX: 'hidden',
            }),
        }}>
        {(USE_SLIDER === true) && (
            <Button
                onClick={prev}
                as={MdArrowBackIos} />
        )}
        <div
            className={'product-list--inner'}
            style={{
                ...minimized === true && ({ minHeight: '360px' }),
                ...minimized !== true && ({ minHeight: '600px' }),
            }}>
            <ul
                ref={productListRef}
                className={`product-list--slider ${USE_SLIDER === true ? 'active' : ''}`}
                style={{
                    ...USE_SLIDER && ({
                        width: `${CONTAINER_WIDTH}px`,
                        left: `calc(${Math.floor(-(selected * ITEM_WIDTH))}px + ${offsetXPercent}%)`,
                        flexWrap: 'nowrap',
                    }),
                    ...alignLeft && ({
                        justifyContent: 'flex-start',
                    }),
                }}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
            >
                {featured === true && (<>
                    {/* Featured section */}
                    <li
                        className={'product-list-item--featured'}
                        style={{
                            ...(USE_SLIDER === true) && ({ flex: `1 0 ${ITEM_WIDTH}px` }),
                            ...(USE_SLIDER === true) && ({ width: `${ITEM_WIDTH}px` }),
                        }}
                    >
                        <Link
                            to={appConfig.featuredProducts?.url}>
                            <div
                                className={'product-list-item-featured--image'}>
                                <img
                                    src={appConfig.featuredProducts?.bannerUrl}
                                    alt={''}
                                    style={{ ...minimized === true && ({ height: '300px' }) }} />
                            </div>
                        </Link>
                    </li>
                </>)}
                {/* Actual list */}
                {productData.map((product) => (
                    <ProductListItem
                        key={product.id}
                        productId={product.id}
                        title={product.title}
                        vendor={product.vendor}
                        price={product.price}
                        compareAtPrice={product.compareAtPrice}
                        availableForSale={product.availableForSale}
                        availableQuantity={product.availableQuantity}
                        bypassNegativeQuantity={product.bypassNegativeQuantity}
                        imageUrl={product.imageUrl}
                        handle={product.handle}
                        inventoryLevelId={product.inventoryLevelId}
                        minimized={minimized}
                        isMobile={isMobile}
                        productWidth={ITEM_WIDTH}
                        avgRating={product.avgRating}
                        totalReviews={product.totalReviews}
                        historyMode={historyMode}
                        useSlider={USE_SLIDER}
                        productCount={productCount}
                        setProductCount={setProductCount}
                        addToOrder={addToOrder}
                        orderId={orderId}
                    />
                ))}
            </ul>
        </div>
        {(USE_SLIDER === true) && (
            <Button
                onClick={next}
                as={MdArrowForwardIos} />
        )}
    </section>);
}

ProductList.propTypes = {
    isMobile: bool,
    products: array,
    featured: bool,
    minimized: bool,
    alignLeft: bool,
    historyMode: bool,
    addToOrder: bool,
    orderId: bool,
};

ProductList.defaultProps = {
    historyMode: false,
    addToOrder: false,
    orderId: null,
};